import clsx from 'clsx';
import Dialog from '../../../organisms/dialogs/Dialog/Dialog';
import TextHtml from '../../../atoms/TextHtml/TextHtml';
import { isDocument } from 'core/utils/lessonItems';
import { CONTENT_TYPES } from 'core/variables/constant';
import React from 'react';
import AttachmentCard from 'core/modules/components/cards/AttachmentCard';
import MaterialPreviewAssigned from './components/MaterialPreviewAssigned';
import MaterialPreviewBodyStudent from './components/MaterialPreviewBodyStudent';
import MaterialPreviewBodyTeacher from './components/MaterialPreviewBodyTeacher';
import MaterialPreviewDueDate from './components/MaterialPreviewDueDate';
import MaterialPreviewHead from './components/MaterialPreviewHead';
import MaterialPreviewMessages from './components/MaterialPreviewMessages';
import MaterialPreviewStudentSidebar from './components/MaterialPreviewStudentSidebar';
import useDialogMaterialPreview from './useDialogMaterialPreview';
import type { PreviewCourse } from './useDialogMaterialPreview';

type LessonItemScore = {
  score: number;
  color: string;
};

type DialogMaterialPreviewProps = {
  course?: PreviewCourse;
  courseGuid?: string;
  evaluationPeriodPublished?: boolean;
  hideReferenceMaterial?: boolean;
  isStudent?: boolean;
  lesson?: Record<string, unknown>;
  lessonItem?: Record<string, unknown>;
  lessonItemScore?: LessonItemScore;
  onClickOpenDetailedView?: (lessonItem: Record<string, unknown>) => void;
  onClose?: () => void;
  open?: boolean;
  sessions?: unknown[];
};

function DialogMaterialPreview({
  course = {},
  courseGuid = '',
  lesson = {},
  lessonItem = {},
  onClose = () => {},
  open = false,
  sessions = [],
  onClickOpenDetailedView = () => {},
  isStudent = false,
  evaluationPeriodPublished = false,
  lessonItemScore = { score: 0, color: '#F2F2F2' },
  hideReferenceMaterial,
}: DialogMaterialPreviewProps): JSX.Element | null {
  const {
    t,
    contentLang,
    material,
    dialogSize,
    isStudentActivityWithAttachments,
    referenceMaterial,
    attachedFiles,
    isActiveSeeDetailsButton,
    isTwoColumn,
    enableLessonItemComments,
    handleSuccess,
    handleGoToDetailedView,
    handleShowVisor,
  } = useDialogMaterialPreview({
    course,
    courseGuid,
    lesson,
    lessonItem,
    onClickOpenDetailedView,
    isStudent,
    hideReferenceMaterial,
  });

  if (!material) return null;

  return (
    <Dialog
      buttonTextSuccess={t('common:See details')}
      className={clsx('dialog-material-preview', { 'dialog-material-preview--student-activity': isStudentActivityWithAttachments })}
      isHeaderHidden={isStudentActivityWithAttachments}
      onClose={onClose}
      onSuccess={handleSuccess}
      isOpen={open}
      size={dialogSize}
      title={isStudentActivityWithAttachments ? undefined : <MaterialPreviewHead contentLang={contentLang} material={material} sessions={sessions || []} t={t} />}
    >
      {!isStudent && isDocument(material?.content_type_guid) && (
        <div className="mb-4">
          <MaterialPreviewDueDate material={material} t={t} />
          <MaterialPreviewAssigned material={material} t={t} />
        </div>
      )}

      <div className="d-flex dialog-material-preview__body">
        {isStudent ? (
          <MaterialPreviewBodyStudent
            course={course}
            onClickOpenDetailedView={onClickOpenDetailedView}
            t={t}
            material={material}
            evaluationPeriodPublished={evaluationPeriodPublished}
            lessonItemScore={lessonItemScore}
            isDialogTwoColumn={isTwoColumn}
          />
        ) : (
          <MaterialPreviewBodyTeacher course={course} material={material} goToDetailedView={handleGoToDetailedView} isTwoColums={isTwoColumn} />
        )}

        {isStudentActivityWithAttachments ? (
          <MaterialPreviewStudentSidebar
            attachedFiles={attachedFiles}
            contentLang={contentLang}
            courseGuid={courseGuid}
            material={material}
            onClose={onClose}
            referenceMaterial={referenceMaterial}
          />
        ) : (
          !material?.content_is_teacher_only &&
          isTwoColumn && (
            <div className="dialog-material-preview__container-messages">
              {referenceMaterial && (
                <div className="dialog-material-preview__container-reference-material">
                  <AttachmentCard
                    content={referenceMaterial}
                    guid={referenceMaterial?.guid}
                    title={referenceMaterial?.name}
                    type={referenceMaterial?.content_type_guid}
                    onDelete={null}
                    onAddContent={null}
                    headerInfo={t(material?.content_type_guid === CONTENT_TYPES.TEST ? 'common:Material adjunto en el quiz' : 'common:Material de referencia')}
                    style="link"
                    onShowVisor={handleShowVisor}
                  />
                </div>
              )}
              {!isStudent && !isDocument(material?.content_type_guid) && (
                <div className="mb-4">
                  <MaterialPreviewDueDate material={material} t={t} />
                  <MaterialPreviewAssigned material={material} t={t} />
                </div>
              )}
              {isStudent && !!material?.description && material?.content_type_guid && (
                <div className="dialog-material-preview__container-description">
                  <TextHtml text={material?.description} />
                </div>
              )}

              {enableLessonItemComments && (
                <MaterialPreviewMessages
                  courseGuid={courseGuid}
                  courseMessageGuid={material?.course_messages_guid}
                  isReadOnly={Boolean(course?.isReadOnly)}
                  t={t}
                />
              )}
            </div>
          )
        )}
      </div>
    </Dialog>
  );
}

export default DialogMaterialPreview;
